import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "COA", to: "/coa" },
  { label: "Affiliates", to: "/affiliates" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
  { label: "Track order", to: "/track" },
];

export default function Header() {
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const loggedIn = !!(user && user.email);
  const accountTo = loggedIn ? "/account" : "/login";

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#efe6d2]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            className="lg:hidden w-10 h-10 -ml-2 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <Link to="/" className="shrink-0" data-testid="logo-home-link">
            <img src="/assets/ethos-logo-emblem.png" alt="Ethos Research Labs — home" width="1752" height="897" className="h-[52px] sm:h-[60px] w-auto object-contain" data-testid="header-logo" />
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[15px] font-medium transition-colors hover:text-foreground pb-1 ${isActive ? "text-foreground" : "text-foreground/70"}`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && <span className="absolute left-0 -bottom-0.5 h-0.5 w-full rounded-full" style={{ background: "var(--gold)" }} />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={() => navigate("/shop")} data-testid="header-search" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors">
            <Search className="w-5 h-5" />
          </button>
          <Link to={accountTo} data-testid="header-account" aria-label={loggedIn ? "My account" : "Sign in"} className="h-10 px-2 sm:px-3 rounded-full flex items-center gap-2 hover:bg-secondary transition-colors">
            <User className="w-5 h-5" />
            <span className="hidden sm:block text-sm font-medium">{loggedIn ? "Account" : "Sign in"}</span>
          </Link>
          <button onClick={() => setDrawerOpen(true)} data-testid="nav-cart-button" className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-[#1a1815]" style={{ background: "var(--gold)" }}>
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile nav panel */}
      {menuOpen && (
        <div className="lg:hidden border-t border-[#efe6d2] bg-white" data-testid="mobile-menu-panel">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-col">
            {NAV.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`py-3 px-2 text-base font-medium border-b border-border/60 last:border-0 ${active ? "text-foreground" : "text-foreground/75"}`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
